// The "Staffing Status" filter on the dashboard's Undeployed Stores list.
//
// A store's staffing status is one of the pipeline states, or nothing at all (the
// column then shows a dash). The filter offers only what the listed stores carry, so
// no choice on its own leads to an empty list, and a store with none can be found
// under "Not set".

import { STAFFING_STATUS_OPTIONS } from '../constants/storeStatus';

/** The filter's value for a store with no staffing status. Never a real status. */
export const STAFFING_STATUS_NOT_SET = '__not_set__';
export const STAFFING_STATUS_NOT_SET_LABEL = 'Not set';

/** What a store's staffing status is to the filter: the status, or the "not set" value. */
export const staffingStatusFilterValue = (status?: string | null): string =>
  (status ?? '').trim() || STAFFING_STATUS_NOT_SET;

/**
 * The choices to offer for `rows`: the statuses they carry, in the order Master Data →
 * Stores lists them, then any status that list does not know (A to Z), then "Not set"
 * when a row has none.
 *
 * A choice that is already ticked stays on offer even after another filter has removed
 * its last store — otherwise it would go on filtering with no row left to untick it on.
 */
export const staffingStatusFilterOptions = (
  rows: ReadonlyArray<{ staffingStatus?: string | null }>,
  selected: readonly string[] = [],
): Array<{ value: string; label: string }> => {
  const present = new Set<string>(selected);
  rows.forEach((r) => present.add(staffingStatusFilterValue(r.staffingStatus)));
  const known: string[] = STAFFING_STATUS_OPTIONS.filter((o) => present.has(o));
  const other = [...present]
    .filter((v) => v !== STAFFING_STATUS_NOT_SET && !known.includes(v))
    .sort((a, b) => a.localeCompare(b));
  const options = [...known, ...other].map((v) => ({ value: v, label: v }));
  if (present.has(STAFFING_STATUS_NOT_SET)) {
    options.push({ value: STAFFING_STATUS_NOT_SET, label: STAFFING_STATUS_NOT_SET_LABEL });
  }
  return options;
};

/** Does a store pass the filter? Nothing ticked = every store passes. */
export const matchesStaffingStatusFilter = (status: string | null | undefined, selected: readonly string[]): boolean =>
  selected.length === 0 || selected.includes(staffingStatusFilterValue(status));
